import { reportPresentation } from './presentationDiagnostics'

export const DISPLAYED_CACHE_LIMIT = 6000
/** Scope-local observable presentation strings; no business-state or cross-tenant sharing. */
export function createChatDisplayCache() {
  const values = new Map<string, string>()
  let evicted = 0
  const listeners = new Map<() => void, { keys?: Set<string>; capture?: () => void }>()
  let revision = 0
  let pendingListeners = new Map<() => void, { keys?: Set<string>; capture?: () => void }>()
  let notificationTask: ReturnType<typeof setTimeout> | undefined
  const key = (locale: string, text: string) => JSON.stringify([locale, text])
  return {
    get: (locale: string, text: string) => values.get(key(locale, text)),
    set(locale: string, source: string, text: string) {
      const changedKeys = new Set<string>()
      for (const entry of [key(locale, source), key(locale, text)]) {
        if (values.get(entry) !== text) changedKeys.add(entry)
        values.set(entry, text)
      }
      while (values.size > DISPLAYED_CACHE_LIMIT) {
        values.delete(values.keys().next().value!)
        // R210: o plano de 203 tarefas guarda 2 entradas por texto; medir quando começa a descartar.
        if (++evicted === 1 || evicted % 1000 === 0)
          reportPresentation({ event: 'displayed_cache_overflow', limit: DISPLAYED_CACHE_LIMIT, evicted })
      }
      if (changedKeys.size > 0) {
        revision++
        listeners.forEach((subscription, listener) => {
          if (!subscription.keys || [...changedKeys].some((entry) => subscription.keys!.has(entry))) {
            subscription.capture?.()
            pendingListeners.set(listener, subscription)
          }
        })
        // A promise burst must not synchronously force an external-store React commit
        // for every field. Keep reads immediate; publish the coherent batch next task.
        notificationTask ??= setTimeout(() => {
          notificationTask = undefined
          const pending = pendingListeners
          pendingListeners = new Map()
          pending.forEach((subscription, listener) => {
            if (listeners.get(listener) === subscription) listener()
          })
        }, 0)
      }
    },
    clear: () => {
      values.clear()
      listeners.clear()
      pendingListeners.clear()
      clearTimeout(notificationTask)
      notificationTask = undefined
    },
    getRevision: () => revision,
    subscribe: (listener: () => void, locale?: string, texts?: string[], capture?: () => void) => {
      listeners.set(
        listener,
        { keys: locale && texts ? new Set(texts.map((text) => key(locale, text))) : undefined, capture },
      )
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
